import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import QuickActionPanel from '../../components/ui/QuickActionPanel';

import WeeklyCalendar from './components/WeeklyCalendar';
import FilterPanel from './components/FilterPanel';
import ClassCard from './components/ClassCard';
import BookingHistoryPanel from './components/BookingHistoryPanel';
import ClassDetailsModal from './components/ClassDetailsModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ClassBooking = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('week');
  const [selectedClass, setSelectedClass] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [filters, setFilters] = useState({
    search: '',
    classType: 'all',
    difficulty: 'all',
    time: 'all',
    instructor: 'all',
    availability: 'all'
  });

  const mockClasses = [
  {
    id: 1,
    name: "Morning HIIT Blast",
    instructor: "Sarah Johnson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1641a596b-1763294716225.png",
    instructorImageAlt: "Professional female fitness instructor with blonde hair in athletic wear smiling confidently",
    instructorBio: "Sarah is a certified HIIT specialist with over 8 years of experience. She focuses on high-intensity workouts that maximize calorie burn and improve cardiovascular fitness.",
    instructorRating: "4.9",
    instructorSpecialties: ["HIIT", "Cardio", "Weight Loss"],
    time: "06:00 AM",
    duration: 45,
    capacity: 20,
    booked: 18,
    difficulty: "Advanced",
    type: "HIIT",
    equipment: ["Mat", "Dumbbells"],
    rating: "4.8",
    reviews: 156,
    description: "High-intensity interval training designed to push your limits and maximize calorie burn. This fast-paced class combines cardio and strength exercises for a full-body workout that will leave you energized for the day ahead.",
    benefits: [
    "Burns up to 500 calories in 45 minutes",
    "Improves cardiovascular endurance",
    "Builds lean muscle mass",
    "Boosts metabolism for hours after workout"],

    isBooked: false,
    isWaitlisted: false,
    isFull: false,
    isMemberOnly: true,
    waitlistPosition: null,
    date: "2026-01-27"
  },
  {
    id: 2,
    name: "Vinyasa Flow Yoga",
    instructor: "Emily Rodriguez",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1efe9c2b4-1763299400143.png",
    instructorImageAlt: "Serene female yoga instructor with dark hair in meditation pose wearing white yoga attire",
    instructorBio: "Emily brings 10 years of yoga practice and teaching experience. She specializes in Vinyasa flow and mindfulness meditation, helping students find balance between strength and flexibility.",
    instructorRating: "5.0",
    instructorSpecialties: ["Vinyasa", "Meditation", "Flexibility"],
    time: "07:30 AM",
    duration: 60,
    capacity: 15,
    booked: 12,
    difficulty: "Intermediate",
    type: "Yoga",
    equipment: ["Yoga Mat", "Blocks"],
    rating: "4.9",
    reviews: 203,
    description: "Flow through a dynamic sequence of poses that synchronize breath with movement. This class builds strength, flexibility, and mindfulness while reducing stress and improving overall well-being.",
    benefits: [
    "Increases flexibility and range of motion",
    "Reduces stress and anxiety",
    "Improves balance and coordination",
    "Enhances mind-body connection"],

    isBooked: true,
    isWaitlisted: false,
    isFull: false,
    isMemberOnly: false,
    waitlistPosition: null,
    date: "2026-01-27"
  },
  {
    id: 3,
    name: "Strength & Conditioning",
    instructor: "Mike Chen",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_197a80582-1763294718036.png",
    instructorImageAlt: "Muscular male strength coach with short black hair in gym attire demonstrating proper form",
    instructorBio: "Mike is a certified strength and conditioning specialist with a background in competitive athletics. He focuses on functional movements and progressive overload to help members achieve their strength goals.",
    instructorRating: "4.8",
    instructorSpecialties: ["Strength Training", "Powerlifting", "Athletic Performance"],
    time: "09:00 AM",
    duration: 60,
    capacity: 12,
    booked: 12,
    difficulty: "Advanced",
    type: "Strength Training",
    equipment: ["Barbell", "Plates", "Bench"],
    rating: "4.7",
    reviews: 89,
    description: "Build serious strength with compound movements and progressive overload. This class focuses on proper form and technique while pushing your limits with challenging weights and rep schemes.",
    benefits: [
    "Increases overall strength and power",
    "Builds lean muscle mass",
    "Improves bone density",
    "Enhances athletic performance"],

    isBooked: false,
    isWaitlisted: false,
    isFull: true,
    isMemberOnly: true,
    waitlistPosition: null,
    date: "2026-01-27"
  },
  {
    id: 4,
    name: "Spin & Burn",
    instructor: "David Kim",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14cdb1530-1769298293888.png",
    instructorImageAlt: "Energetic male cycling instructor with athletic build in cycling gear with motivational expression",
    instructorBio: "David is a former competitive cyclist who brings high energy and motivation to every class. His cycling workouts are designed to improve endurance, burn calories, and build lower body strength.",
    instructorRating: "4.9",
    instructorSpecialties: ["Cycling", "Endurance", "Cardio"],
    time: "05:30 PM",
    duration: 45,
    capacity: 25,
    booked: 20,
    difficulty: "Intermediate",
    type: "Cycling",
    equipment: ["Cycling Shoes"],
    rating: "4.8",
    reviews: 178,
    description: "High-energy indoor cycling class with motivating music and challenging intervals. Ride through hills, sprints, and endurance segments that will test your limits and leave you dripping with sweat.",
    benefits: [
    "Burns 400-600 calories per session",
    "Low-impact cardio workout",
    "Builds leg strength and endurance",
    "Improves cardiovascular fitness"],

    isBooked: false,
    isWaitlisted: false,
    isFull: false,
    isMemberOnly: false,
    waitlistPosition: null,
    date: "2026-01-27"
  },
  {
    id: 5,
    name: "Core Power Pilates",
    instructor: "Lisa Patel",
    instructorImage: "https://images.unsplash.com/photo-1717500252573-d31d4bf5ddf1",
    instructorImageAlt: "Graceful female Pilates instructor with brown hair demonstrating core exercise on reformer machine",
    instructorBio: "Lisa is a certified Pilates instructor with expertise in core strengthening and rehabilitation. She helps members develop a strong foundation through controlled movements and proper breathing techniques.",
    instructorRating: "5.0",
    instructorSpecialties: ["Pilates", "Core Strength", "Posture"],
    time: "06:30 PM",
    duration: 50,
    capacity: 18,
    booked: 15,
    difficulty: "Beginner",
    type: "Pilates",
    equipment: ["Mat", "Resistance Band"],
    rating: "4.9",
    reviews: 142,
    description: "Strengthen your core and improve posture with controlled Pilates movements. This class focuses on building a strong foundation through precise exercises that target deep stabilizing muscles.",
    benefits: [
    "Strengthens core muscles",
    "Improves posture and alignment",
    "Increases flexibility",
    "Reduces back pain"],

    isBooked: false,
    isWaitlisted: false,
    isFull: false,
    isMemberOnly: true,
    waitlistPosition: null,
    date: "2026-01-27"
  },
  {
    id: 6,
    name: "Boxing Bootcamp",
    instructor: "Sarah Johnson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1641a596b-1763294716225.png",
    instructorImageAlt: "Professional female fitness instructor with blonde hair in athletic wear smiling confidently",
    instructorBio: "Sarah is a certified HIIT specialist with over 8 years of experience. She focuses on high-intensity workouts that maximize calorie burn and improve cardiovascular fitness.",
    instructorRating: "4.9",
    instructorSpecialties: ["Boxing", "HIIT", "Cardio"],
    time: "07:00 PM",
    duration: 60,
    capacity: 16,
    booked: 16,
    difficulty: "Intermediate",
    type: "Boxing",
    equipment: ["Boxing Gloves", "Hand Wraps"],
    rating: "4.7",
    reviews: 95,
    description: "Learn boxing fundamentals while getting an intense full-body workout. This class combines technical skill development with high-intensity cardio for maximum calorie burn and stress relief.",
    benefits: [
    "Full-body cardio workout",
    "Improves coordination and reflexes",
    "Builds upper body strength",
    "Great stress relief"],

    isBooked: false,
    isWaitlisted: true,
    isFull: true,
    isMemberOnly: false,
    waitlistPosition: 3,
    date: "2026-01-27"
  }];


  const mockBookingHistory = [
  {
    id: 101,
    className: "Vinyasa Flow Yoga",
    instructor: "Emily Rodriguez",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1efe9c2b4-1763299400143.png",
    instructorImageAlt: "Serene female yoga instructor with dark hair in meditation pose wearing white yoga attire",
    date: "2026-01-27T07:30:00",
    time: "07:30 AM",
    duration: 60,
    status: "upcoming"
  },
  {
    id: 102,
    className: "Morning HIIT Blast",
    instructor: "Sarah Johnson",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1641a596b-1763294716225.png",
    instructorImageAlt: "Professional female fitness instructor with blonde hair in athletic wear smiling confidently",
    date: "2026-01-24T06:00:00",
    time: "06:00 AM",
    duration: 45,
    status: "completed"
  },
  {
    id: 103,
    className: "Spin & Burn",
    instructor: "David Kim",
    instructorImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14cdb1530-1769298293888.png",
    instructorImageAlt: "Energetic male cycling instructor with athletic build in cycling gear with motivational expression",
    date: "2026-01-23T05:30:00",
    time: "05:30 PM",
    duration: 45,
    status: "completed"
  }];


  const [classes, setClasses] = useState(mockClasses);
  const [bookingHistory, setBookingHistory] = useState(mockBookingHistory);

  const filterClasses = () => {
    return classes?.filter((classItem) => {
      const matchesSearch = classItem?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      classItem?.instructor?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesType = filters?.classType === 'all' || classItem?.type?.toLowerCase() === filters?.classType;
      const matchesDifficulty = filters?.difficulty === 'all' || classItem?.difficulty === filters?.difficulty;
      const matchesInstructor = filters?.instructor === 'all' || classItem?.instructor?.toLowerCase()?.replace(' ', '-') === filters?.instructor;

      let matchesTime = true;
      if (filters?.time !== 'all') {
        const hour = parseInt(classItem?.time?.split(':')?.[0]);
        const isPM = classItem?.time?.includes('PM');
        const hour24 = isPM && hour !== 12 ? hour + 12 : hour;

        if (filters?.time === 'morning') matchesTime = hour24 >= 6 && hour24 < 12;
        if (filters?.time === 'afternoon') matchesTime = hour24 >= 12 && hour24 < 17;
        if (filters?.time === 'evening') matchesTime = hour24 >= 17 && hour24 < 21;
      }

      let matchesAvailability = true;
      if (filters?.availability === 'available') matchesAvailability = !classItem?.isFull;
      if (filters?.availability === 'waitlist') matchesAvailability = classItem?.isFull;

      return matchesSearch && matchesType && matchesDifficulty && matchesTime &&
      matchesInstructor && matchesAvailability;
    });
  };

  const filteredClasses = filterClasses();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      classType: 'all',
      difficulty: 'all',
      time: 'all',
      instructor: 'all',
      availability: 'all'
    });
  };

  const handleBookClass = (classId) => {
    setClasses((prev) => prev?.map((c) =>
    c?.id === classId ? { ...c, isBooked: true, booked: c?.booked + 1 } : c
    ));

    const bookedClass = classes?.find((c) => c?.id === classId);
    if (bookedClass) {
      const newBooking = {
        id: Date.now(),
        className: bookedClass?.name,
        instructor: bookedClass?.instructor,
        instructorImage: bookedClass?.instructorImage,
        instructorImageAlt: bookedClass?.instructorImageAlt,
        date: `${bookedClass?.date}T${bookedClass?.time}`,
        time: bookedClass?.time,
        duration: bookedClass?.duration,
        status: "upcoming"
      };
      setBookingHistory((prev) => [newBooking, ...prev]);
    }

    setSuccessMessage('Class booked successfully! Check your email for confirmation.');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const handleCancelBooking = (classId) => {
    setClasses((prev) => prev?.map((c) =>
    c?.id === classId ? { ...c, isBooked: false, booked: Math.max(0, c?.booked - 1) } : c
    ));

    setBookingHistory((prev) => prev?.filter((b) => b?.id !== classId));

    setSuccessMessage('Booking cancelled successfully. Refund will be processed within 3-5 business days.');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const handleJoinWaitlist = (classId) => {
    setClasses((prev) => prev?.map((c) =>
    c?.id === classId ? { ...c, isWaitlisted: true, waitlistPosition: 1 } : c
    ));

    setSuccessMessage('Added to waitlist! You\'ll be notified if a spot opens up.');
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const handleViewDetails = (classId) => {
    const classData = classes?.find((c) => c?.id === classId);
    setSelectedClass(classData);
    setIsModalOpen(true);
  };

  const handleQuickAction = (actionId) => {
    if (actionId === 'book-class') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="member" userName="John Doe" />
      <div className="page-content-wrapper">
        <DashboardNavigation userRole="member" />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        {showSuccessMessage &&
        <div className="mb-6 p-4 rounded-lg bg-success/10 border border-success/20 flex items-start gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success flex-1">{successMessage}</p>
            <button
            onClick={() => setShowSuccessMessage(false)}
            className="flex-shrink-0 text-success hover:text-success/80">

              <Icon name="X" size={18} />
            </button>
          </div>
        }

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
            Book Your Classes
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Reserve your spot in upcoming fitness classes and manage your bookings
          </p>
        </div>

        <div className="space-y-6 md:space-y-8">
          <WeeklyCalendar
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onViewChange={setCalendarView}
            currentView={calendarView} />


          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters} />


          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-4 md:space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  Available Classes ({filteredClasses?.length})
                </h2>
              </div>

              {filteredClasses?.length === 0 ?
              <div className="card flex flex-col items-center justify-center py-12 md:py-16">
                  <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" />
                  <p className="mt-4 text-sm md:text-base text-muted-foreground text-center">
                    No classes found matching your filters. Try adjusting your search criteria.
                  </p>
                  <Button
                  variant="outline"
                  className="mt-6"
                  onClick={handleClearFilters}
                  iconName="RotateCcw"
                  iconPosition="left">

                    Clear Filters
                  </Button>
                </div> :

              <div className="space-y-4 md:space-y-6">
                  {filteredClasses?.map((classItem) =>
                <ClassCard
                  key={classItem?.id}
                  classData={classItem}
                  onBook={handleBookClass}
                  onCancel={handleCancelBooking}
                  onJoinWaitlist={handleJoinWaitlist}
                  onViewDetails={handleViewDetails} />

                )}
                </div>
              }
            </div>

            <div className="lg:col-span-1">
              <BookingHistoryPanel
                bookings={bookingHistory}
                onViewDetails={handleViewDetails}
                onCancelBooking={handleCancelBooking} />

            </div>
          </div>
        </div>
      </main>
      <ClassDetailsModal
        classData={selectedClass}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onBook={handleBookClass}
        onCancel={handleCancelBooking}
        onJoinWaitlist={handleJoinWaitlist} />

      <QuickActionPanel userRole="member" onAction={handleQuickAction} />
    </div>);

};

export default ClassBooking;